import React, { useState, useEffect } from 'react';
import { updateSection, addItem, updateItem, deleteItem } from '../../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Plus,
  Trash2,
  Save,
  Pencil,
  X,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Type,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

const SectionEditor = ({ sectionId, sectionData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(null); // ID of the item being edited in an array
  const [tempData, setTempData] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  useEffect(() => {
    setTempData(null);
    setIsEditing(null);
    setIsAddingNew(false);
  }, [sectionId]);

  const isArray = Array.isArray(sectionData);

  // Helper to render input based on field name and value
  const renderInput = (key, value, onChange) => {
    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
    
    if (key === 'id') return null; // Don't show ID

    if (typeof value === 'boolean') {
        return (
            <div key={key} className="flex items-center space-x-2 py-2">
                <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(key, e.target.checked)}
                    className="h-4 w-4 rounded border-slate-700 bg-slate-800 text-amber-500"
                />
                <Label className="text-slate-300">{label}</Label>
            </div>
        );
    }

    if (key === 'description' || key === 'bio' || key === 'content' || key === 'excerpt' || key === 'answer') {
      return (
        <div key={key} className="space-y-2">
          <Label className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{label}</Label>
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(key, e.target.value)}
            className="bg-slate-800 border-slate-700 text-white focus:ring-amber-500/50"
            rows={4}
          />
        </div>
      );
    }

    if (key === 'types' || key === 'features' || key === 'education' || key === 'tags') {
      // Handle simple string arrays or comma separated strings
      const displayValue = Array.isArray(value) ? value.join(', ') : value;
      return (
        <div key={key} className="space-y-2">
          <Label className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{label} (comma separated)</Label>
          <Input
            value={displayValue || ''}
            onChange={(e) => {
                const arr = e.target.value.split(',').map(s => s.trim()).filter(s => s !== '');
                onChange(key, arr);
            }}
            className="bg-slate-800 border-slate-700 text-white focus:ring-amber-500/50"
          />
        </div>
      );
    }

    return (
      <div key={key} className="space-y-2">
        <Label className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{label}</Label>
        <div className="relative">
            {key.toLowerCase().includes('image') ? (
                <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
            ) : key.toLowerCase().includes('link') ? (
                <LinkIcon className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
            ) : (
                <Type className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
            )}
            <Input
                value={value || ''}
                onChange={(e) => onChange(key, e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white focus:ring-amber-500/50"
            />
        </div>
      </div>
    );
  };

  const renderNode = (key, value, onChange) => {
    if (key === 'id') return null;

    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object') {
        // Array of objects (like segments, services, process)
        return (
          <div key={key} className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white capitalize">{key.replace(/([A-Z])/g, ' $1')}</h3>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="text-amber-500 border-amber-500 hover:bg-amber-500/10"
                onClick={() => {
                  const newItem = value.length > 0 ? { ...value[0] } : { id: '' };
                  Object.keys(newItem).forEach(k => newItem[k] = k === 'id' ? String(Date.now()) : '');
                  onChange(key, [...value, newItem]);
                }}
              >
                <Plus size={14} className="mr-1" /> Add {key}
              </Button>
            </div>
            {value.map((item, index) => (
              <Card key={item.id || index} className="bg-slate-800 border-slate-700 p-4 relative">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                  onClick={() => {
                    const newArr = [...value];
                    newArr.splice(index, 1);
                    onChange(key, newArr);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8">
                  {Object.entries(item).map(([k, v]) => 
                    renderNode(k, v, (subKey, subVal) => {
                      const newArr = [...value];
                      newArr[index] = { ...item, [subKey]: subVal };
                      onChange(key, newArr);
                    })
                  )}
                </div>
              </Card>
            ))}
          </div>
        );
      } else {
        // Array of strings or empty array
        return renderInput(key, value, onChange);
      }
    } else if (typeof value === 'object' && value !== null) {
      // Nested object (like hero)
      return (
        <Card key={key} className="bg-slate-800 border-slate-700 mt-6 shadow-md">
          <CardHeader className="py-4 px-6 border-b border-slate-700 bg-slate-800/50">
            <CardTitle className="text-md text-amber-500 font-bold capitalize">{key.replace(/([A-Z])/g, ' $1')}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(value).map(([k, v]) => 
                renderNode(k, v, (subKey, subVal) => {
                  onChange(key, { ...value, [subKey]: subVal });
                })
              )}
            </div>
          </CardContent>
        </Card>
      );
    } else {
      // Primitive
      return renderInput(key, value, onChange);
    }
  };

  const handleSaveObject = async () => {
    try {
      await updateSection(sectionId, tempData || sectionData);
      toast.success('Section updated successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update section');
    }
  };

  const handleSaveItem = async (id) => {
    try {
      if (id) {
        await updateItem(sectionId, id, tempData);
        toast.success('Item updated successfully');
        setIsEditing(null);
      } else {
        await addItem(sectionId, tempData);
        toast.success('New item added successfully');
        setIsAddingNew(false);
      }
      onUpdate();
      setTempData(null);
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await deleteItem(sectionId, id);
      toast.success('Item deleted successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Render Object Editor
  if (!isArray) {
    const data = tempData || sectionData;
    return (
      <Card className="bg-slate-900 border-slate-800 shadow-xl">
        <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold text-white">Edit Content</CardTitle>
          <Button onClick={handleSaveObject} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Save size={18} className="mr-2" /> Save Changes
          </Button>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {Object.entries(data).map(([key, value]) => {
            return renderNode(key, value, (k, v) => setTempData({ ...data, [k]: v }));
          })}
        </CardContent>
      </Card>
    );
  }

  // Render Array Editor
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Manage Items</h2>
        {!isAddingNew && (
          <Button
            onClick={() => {
              setIsAddingNew(true);
              setIsEditing(null);
              // Initialize empty item based on first item schema
              const schema = sectionData.length > 0 ? { ...sectionData[0] } : {};
              Object.keys(schema).forEach(k => schema[k] = k === 'id' ? '' : '');
              setTempData(schema);
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus size={18} className="mr-2" /> Add New Item
          </Button>
        )}
      </div>

      {isAddingNew && (
        <Card className="bg-slate-900 border-amber-500/30 border-2 shadow-2xl">
          <CardHeader className="bg-amber-500/5 border-b border-slate-800">
            <CardTitle className="text-white">Add New Entry</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {Object.entries(tempData).map(([key, value]) => (
              renderNode(key, value, (k, v) => setTempData({ ...tempData, [k]: v }))
            ))}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button variant="ghost" onClick={() => setIsAddingNew(false)} className="text-slate-400 hover:text-white">
                Cancel
              </Button>
              <Button onClick={() => handleSaveItem(null)} className="bg-amber-600 hover:bg-amber-700 text-white">
                Create Item
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {sectionData.map((item) => (
          <Card key={item.id} className={`bg-slate-900 border-slate-800 transition-all ${isEditing === item.id ? 'ring-2 ring-amber-500/50' : ''}`}>
            <CardContent className="p-4">
              {isEditing === item.id ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    {Object.entries(tempData).map(([key, value]) => (
                      renderNode(key, value, (k, v) => setTempData({ ...tempData, [k]: v }))
                    ))}
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                    <Button variant="ghost" onClick={() => setIsEditing(null)} className="text-slate-400 hover:text-white">
                      Cancel
                    </Button>
                    <Button onClick={() => handleSaveItem(item.id)} className="bg-amber-600 hover:bg-amber-700 text-white">
                      <Save size={18} className="mr-2" /> Update Item
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {item.image && (
                      <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-800" />
                    )}
                    <div>
                      <h4 className="font-bold text-white text-lg">{item.title || item.name || item.question || item.label}</h4>
                      <p className="text-slate-400 text-sm line-clamp-1">{item.description || item.role || item.company || item.subtitle || item.answer}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setIsEditing(item.id);
                        setTempData({ ...item });
                        setIsAddingNew(false);
                      }}
                      className="text-slate-400 hover:text-amber-500 hover:bg-amber-500/10"
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item.id)}
                      className="text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SectionEditor;
